import { Location } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import ObjectId from 'bson-objectid';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, interval, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { BroadcastAction, BroadcastService } from '../broadcast.service';
import {
  CameraAngle,
  Game,
  GameOfficial,
  Video,
  VideoFormat,
  VideoVariantType
} from '../domain/game';
import {
  collectsEventType,
  isFloorball,
  regularPeriodCount
} from '../domain/sport';
import { turnoverScenarios, turnoverTypes } from '../domain/turnover';
import {
  PersonalPenaltyOption,
  penaltyDurationsFor,
  personalPenaltiesFor
} from '../domain/penalty';
import {
  faceOffAsRestart,
  GameRestartOption,
  GameRestartReason,
  GameRestartType,
  gameRestartReasonsFor,
  gameRestartTypesFor
} from '../domain/game-restart';
import {
  DefensiveReaction,
  defensiveReactions,
  EventType,
  hasRinkPosition,
  isClockEvent,
  GameEvent,
  GameIncidentReason,
  GameIncidentSituation,
  GamePeriod,
  InterruptionType,
  OddMenRushDetail,
  OfficialsPenaltyCallSeverity,
  OfficialsUncalledPenaltySeverity,
  PenaltyDuration,
  StrengthState,
  VideoClip,
  VideoClipType
} from '../domain/game-event';
import { Player } from '../domain/player';
import { Players } from '../domain/players';
import { Shift } from '../domain/shift';
import { Team } from '../domain/team';
import { EventsComponent } from '../events/events.component';
import { AlertService } from '../services/alert.service';
import { EventValidationService } from '../services/event-validation.service';
import { EventService } from '../services/event.service';
import { GameTimeService } from '../services/game-time.service';
import { GameService } from '../services/game.service';
import { PuckPossessionStateService } from '../services/puck-possession-state.service';
import { ShiftsService } from '../services/shifts.service';
import { StrengthStateService } from '../services/strength-state.service';
import { VideoService } from '../services/video.service';
import { CameraAnglesSelectDialogComponent } from '../shared/camera-angles-select/camera-angles-select-dialog.component';
import { PlayerSummaryComponent } from '../shared/player-summary/player-summary.component';
import { VideoPlayerTrimDialogComponent } from '../video-player-trim/video-player-trim-dialog/video-player-trim-dialog.component';

import {
  assist1PlayerNumberChange,
  assist2PlayerNumberChange,
  deflectorPlayerNumberChange,
  eventIdChange,
  eventTypeChange,
  faceoffOpponentPlayerNumberChange,
  faceoffOutcomePositionChange,
  fouledPlayerNumberChange,
  turnoverOpponentPlayerNumberChange,
  gameIncidentReasonChange,
  gameIncidentSituationChange,
  gameIncidentTypeChange,
  hasNetTrafficChange,
  hasScreenChange,
  highlightPlaybackChange,
  highlightRatingChange,
  highlightTypeChange,
  interruptionTypeChange,
  netTrafficCauserPlayerNumberChange,
  oddMenRushDetailChange,
  defensiveReactionChange,
  gameRestartReasonChange,
  turnoverScenarioChange,
  turnoverTypeChange,
  gameRestartTypeChange,
  official1NumberChange,
  official2NumberChange,
  officialsCallActionChange,
  officialsCallSituationChange,
  passOutcomeChange,
  passReceiverPlayerNumberChange,
  passReceiverPositionChange,
  passTypeChange,
  periodChange,
  playerNumberChange,
  positionChange,
  positionPitchChange,
  resetHardState as resetHardStateGameEvent,
  resetState as resetStateGameEvent,
  screenerPlayerNumberChange,
  shotBlockerPlayerNumberChange,
  shotBlockerPositionChange,
  shotOutcomeChange,
  shotScenarioChange,
  shotTypeChange,
  teamChange,
  teamFaceoffOutcomeChange,
  videoTagChange
} from '../state/actions/game-event.action';
import {
  gameIdChange,
  resetHardState as resetHardStateGame,
  resetState as resetStateGame,
  videoTimeExternalChange
} from '../state/actions/game.action';
import { GlobalState } from '../state/reducers';
import {
  selectAssist1PlayerNumber,
  selectAssist2PlayerNumber,
  selectDeflectorPlayerNumber,
  selectEventType,
  selectFaceOffOpponentPlayerNumber,
  selectFaceoffOutcomePosition,
  selectFouledPlayerNumber,
  selectTurnoverOpponentPlayerNumber,
  selectOfficial1Id,
  selectOfficial2Id,
  selectGameEventState,
  selectGameIncidentReason,
  selectGameIncidentSituation,
  selectGameIncidentType,
  selectGameTime,
  selectHas_net_traffic,
  selectHas_screen,
  selectHighlightPlayback,
  selectHighlightRating,
  selectHighlightType,
  selectInterruption_type,
  selectNetImpact,
  selectNetTrafficCauserPlayerNumber,
  selectOddMenRushDetail,
  selectDefensiveReaction,
  selectGameRestartReason,
  selectTurnoverScenario,
  selectTurnoverType,
  selectGameRestartType,
  selectOfficialsCallAction,
  selectOfficialsCallSituation,
  selectPassOutcome,
  selectPassReceiverPlayerNumber,
  selectPassReceiverPosition,
  selectPassType,
  selectPenaltyDuration,
  selectPenaltyId,
  selectPenaltyType,
  selectPeriod,
  selectPlayerNumber,
  selectPosition,
  selectPositionPitch,
  selectScreenerPlayerNumber,
  selectShotBlockerPlayerNumber,
  selectShotBlockerPosition,
  selectShotOutcome,
  selectShotScenario,
  selectShotType,
  selectStrengthState,
  selectTeam,
  selectTeamFaceOffOutcome,
  selectVideoReviewType,
  selectVideoTag,
  selectVideoTime,
  selectAttackScenario
} from '../state/reducers/game-event.reducer';
import { selectGameId, selectGameState } from '../state/reducers/game.reducer';
import { GameEventInterruptionTypeService } from '../services/game-event-interruption-type.service';
import { GameIncidentService } from '../services/game-incident.service';
import { DomainService } from '../services/domain.service';

interface EventTypeOption {
  value: EventType;
  label: string;
  floorballLabel?: string;
  condition?: (game: Game) => boolean;
}

@Component({
  selector: 'app-game-events',
  templateUrl: './game-events.component.html',
  styleUrls: ['./game-events.component.css']
})
export class GameEventsComponent implements OnInit, OnDestroy {
  @ViewChild(EventsComponent, { static: true })
  eventsComponent: EventsComponent;

  readonly allEventTypeOptions: EventTypeOption[] = [
    { value: 'time_on_ice', label: 'Time on Ice' },
    { value: 'face_off', label: 'Face Off' },
    { value: 'interruption', label: 'Interruption' },
    { value: 'gameRestart', label: 'Game Restart' },
    {
      value: 'puckPossession',
      label: 'Puck Possession',
      floorballLabel: 'Ball Possession'
    },
    { value: 'turnover', label: 'Turnover' },
    { value: 'pass', label: 'Pass' },
    { value: 'shot', label: 'Shot' },
    { value: 'penalty', label: 'Penalty' },
    { value: 'videoTag', label: 'Video Tag' },
    { value: 'oddMenRush', label: 'Odd Men Rush' },
    {
      value: 'highlight',
      label: 'Highlight',
      condition: (game) => game.isHighlightCollection
    },
    {
      value: 'game_incident',
      label: 'Game Incident',
      condition: (game) => game.hasOfficiatingCollection()
    }
  ];

  eventTypeLabel(option: EventTypeOption): string {
    return (
      (isFloorball(this.game?.sport) && option.floorballLabel) || option.label
    );
  }

  get visibleEventTypeOptions() {
    return this.allEventTypeOptions.filter(
      (option) =>
        collectsEventType(this.game?.sport, option.value) &&
        (!option.condition || option.condition(this.game))
    );
  }

  gameTime = 0;
  event = {} as GameEvent;
  // The event as loaded for an edit, before the form changed it.
  private savedEvent: GameEvent;
  game: Game;
  teams: string[];
  players: string[];
  homeTeamPlayers: string[];
  awayTeamPlayers: string[];
  opponentPlayers: string[];

  infractionTypes: { key: string; value: string }[] = [];

  filterablePlayers: Player[] = [];
  filteredPlayerId: string;

  shifts: Shift[] = [];

  get penaltyDurations(): PenaltyDuration[] {
    return penaltyDurationsFor(this.game?.sport);
  }

  get personalPenalties(): PersonalPenaltyOption[] {
    return personalPenaltiesFor(this.game?.sport);
  }
  strengthStates: StrengthState[] = [
    '5-5',
    '5-4',
    '4-5',
    '4-4',
    '5-3',
    '3-5',
    '4-3',
    '3-4',
    '3-3'
  ];
  oddMenRushDetails: OddMenRushDetail[] = [
    '4-3',
    '4-2',
    '4-1',
    '4-0',
    '3-2',
    '3-1',
    '3-0',
    '2-1',
    '2-0',
    '1-0'
  ];

  readonly turnoverTypes = turnoverTypes;
  readonly turnoverScenarios = turnoverScenarios;

  // Floorball games collected before the floorball setup have face-offs where
  // play resumed with a hit-in or free hit; validation points them out.
  // Offered on the winning half: its team takes the hit-in or free hit.
  get canConvertToRestart(): boolean {
    return (
      this.isEditMode &&
      isFloorball(this.game?.sport) &&
      this.event.eventType === 'face_off' &&
      this.event.teamFaceOffOutcome === 'win'
    );
  }

  async convertToRestart(gameRestartType: GameRestartType) {
    const faceOff = this.event;
    let restart: GameEvent;
    try {
      restart = await firstValueFrom(
        this.eventService.save(faceOffAsRestart(faceOff, gameRestartType))
      );
    } catch (error) {
      this.alertService.showError(
        'Converting the face-off failed: ' + error.message
      );
      return;
    }
    try {
      // the server deletes the paired face-off of the other team with it
      await firstValueFrom(
        this.eventService.delete(faceOff.gameId, faceOff._id)
      );
      this.alertService.showInfo('Face-off converted to a restart');
    } catch (error) {
      if (await this.isStillSaved(faceOff)) {
        await this.undoConvertedRestart(restart, error);
        return;
      }
      this.alertService.showError(
        'The restart is saved, but the face-off could not be deleted ' +
          'completely; check the face-offs in the events list: ' +
          error.message
      );
    }
    this.gameTimeService.deleteTimeEvent(faceOff._id, true);
    this.gameTimeService.addAndDispatchTimeEvent(restart, restart.videoTime);
    this.eventsComponent.loadEvents();
    await this.reset();
  }

  // The server deletes the face-off before its pair, so a failed delete may
  // have removed it already. Only a face-off known to be saved may take the
  // restart back with it; otherwise the restart is all that is left of it.
  private async isStillSaved(event: GameEvent): Promise<boolean> {
    try {
      await firstValueFrom(this.eventService.getEvent(event.gameId, event._id));
      return true;
    } catch {
      return false;
    }
  }

  // The face-off could not be deleted, so the restart saved for it goes again.
  private async undoConvertedRestart(restart: GameEvent, error: Error) {
    try {
      await firstValueFrom(
        this.eventService.delete(restart.gameId, restart._id)
      );
      this.alertService.showError(
        'Converting the face-off failed: ' + error.message
      );
    } catch {
      this.alertService.showError(
        'Converting the face-off failed and both the face-off and the restart ' +
          'are saved now; delete one of them: ' +
          error.message
      );
      this.eventsComponent.loadEvents();
    }
  }

  // Keeps a field that does not apply off the event. A value the saved event
  // carries is cleared instead, as the server only overwrites the fields it
  // receives (e.g. a personal penalty set to none).
  private leaveOff(field: keyof GameEvent) {
    if (
      this.event._id &&
      this.savedEvent?._id === this.event._id &&
      this.savedEvent[field] != null
    ) {
      Object.assign(this.event, { [field]: null });
    } else {
      delete this.event[field];
    }
  }

  readonly defensiveReactions: {
    value: DefensiveReaction;
    label: string;
    hotkey: string;
  }[] = defensiveReactions;

  get gameRestartTypes(): GameRestartOption<GameRestartType>[] {
    return gameRestartTypesFor(this.game?.sport);
  }

  get gameRestartReasons(): GameRestartOption<GameRestartReason>[] {
    return gameRestartReasonsFor(this.game?.sport);
  }

  readonly eventTypes: any[] = [];

  private readonly gameIncidentDescriptions: Record<
    GameIncidentReason,
    string
  > = {
    abuseOfOfficials: 'Abuse of Officials',
    boarding: 'Boarding',
    bodycheck: 'Bodycheck',
    buttEnding: 'Butt-ending',
    charging: 'Charging',
    checkFromBehind: 'Check From Behind',
    checkToTheHeadOrNeck: 'Check To The Head or Neck',
    clipping: 'Clipping',
    crossChecking: 'Cross Checking',
    delayingTheGame: 'Delaying the Game',
    divingOrEmbellishment: 'Diving or Embellishment',
    elbowing: 'Elbowing',
    fighting: 'Fighting',
    grabbingFaceMask: 'Grabbing Face-mask',
    headButting: 'Head-butting',
    highSticking: 'High-Sticking',
    holding: 'Holding',
    hooking: 'Hooking',
    interference: 'Interference',
    kicking: 'Kicking',
    kneeing: 'Kneeing',
    penaltyShot: 'Penalty Shot',
    roughing: 'Roughing',
    slashing: 'Slashing',
    slewFooting: 'Slew-footing',
    spearing: 'Spearing',
    tripping: 'Tripping',
    unsportsmanlike: 'Unsportsmanlike',
    other: 'Other'
  };

  private readonly gameIncidentSituations: Record<
    GameIncidentSituation,
    string
  > = {
    refereeTangledUpInPlay: 'Referee Tangled Up In Play',
    officialTalkingToPlayerOrCoaches: 'Official Talking to Player or Coaches',
    refereeHuddle: 'Referee Huddle',
    scrum: 'Scrum',
    playerControl: 'Player Control',
    handpass: 'Handpass',
    highStickingThePuck: 'High-sticking the Puck'
  };

  readonly gameIncidentSituationLabels = Object.entries(
    this.gameIncidentSituations
  ).map(([key, label]) => ({
    key: key as GameIncidentSituation,
    label
  }));

  readonly allGameIncidentReasons = Object.entries(
    this.gameIncidentDescriptions
  ).map(([key, reason]) => ({
    key: key as GameIncidentReason,
    description: reason
  }));

  allOfficialsUncalledPenaltySeverities: {
    key: OfficialsUncalledPenaltySeverity;
    description: string;
  }[] = [
    { key: 'minor', description: 'Minor' },
    { key: 'major', description: 'Major' }
  ];

  allOfficialsPenaltyCallSeverities: {
    key: OfficialsPenaltyCallSeverity;
    description: string;
  }[] = [
    { key: 'unspecified', description: 'Unspecified' },
    { key: 'penalty-shot', description: 'Penalty Shot (0)' },
    { key: 'minor', description: 'Minor (2)' },
    { key: 'double-minor', description: 'Double Minor (2+2)' },
    { key: 'major', description: 'Major (5)' },
    { key: 'misconduct', description: 'Misconduct (10)' },
    { key: 'game-misconduct', description: 'Game Misconduct (20)' }
  ];

  private componentDestroyed$: Subject<void> = new Subject();

  cameraAngle: CameraAngle;
  format: VideoFormat;
  variant: VideoVariantType;
  videoTrim: Video;

  @ViewChild('videoElement') videoElement: ElementRef;

  // Make the enum accessible to the template by adding a reference in the class
  VideoClipType = VideoClipType;

  videoClipType: VideoClipType;

  allOfficials: GameOfficial[] = [];
  filteredOfficials: GameOfficial[] = [];

  constructor(
    public eventService: EventService,
    private location: Location,
    private gameService: GameService,
    private eventValidationService: EventValidationService,
    private gameTimeService: GameTimeService,
    private puckPossessionStateService: PuckPossessionStateService,
    private strengthStateService: StrengthStateService,
    private shiftsService: ShiftsService,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private logger: NGXLogger,
    private title: Title,
    private dialog: MatDialog,
    private broadcast: BroadcastService,
    private videoService: VideoService,
    private store: Store<GlobalState>,
    private gameEventInterruptionTypeService: GameEventInterruptionTypeService,
    private gameIncidentService: GameIncidentService,
    private domainService: DomainService
  ) {}

  ngOnInit(): void {
    const gameId = this.route.snapshot.params['gameId'];
    this.strengthStateService.init(gameId);
    this.gameTimeService.init(gameId);
    this.puckPossessionStateService.init(gameId);
    this.initForGame((this.route.snapshot.data as any).game);
    this.fetchInfractionTypes();
    this.subscribeToReduxEvents();
    this.startWallClock();
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(null);
  }

  private subscribeToReduxEvents() {
    this.event.videoTime = 0;
    this.store
      .select(selectGameId)
      .pipe(takeUntil(this.componentDestroyed$), take(1))
      .subscribe(async (gameId) => {
        if (gameId !== this.game._id) {
          this.logger.info(
            'reset redux state for game switch',
            gameId,
            this.game._id
          );
          await this.resetHard();
        }
      });
    this.store
      .select(selectEventType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(async (newValue) => {
        this.event.eventType = newValue;
        if (this.event.eventType === 'penalty' && !this.event.penaltyType) {
          this.event.penaltyType = 'start';
        }
        await this.handleDraftEvent();
      });
    this.store
      .select(selectPeriod)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.period = newValue));
    this.store
      .select(selectOddMenRushDetail)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.oddMenRushDetail = newValue));
    this.store
      .select(selectDefensiveReaction)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.defensiveReaction = newValue));
    this.store
      .select(selectGameRestartType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.gameRestartType = newValue));
    this.store
      .select(selectGameRestartReason)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.gameRestartReason = newValue));
    this.store
      .select(selectTurnoverType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.turnoverType = newValue));
    this.store
      .select(selectTurnoverScenario)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.turnoverScenario = newValue));
    this.store
      .select(selectTeam)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(async (newValue) => {
        this.handleTeamMessage(newValue);
        await this.handleDraftEvent();
      });
    this.store
      .select(selectVideoTime)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        this.updateVideoTime(newValue);
      });
    this.store
      .select(selectStrengthState)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((strengthState) => (this.event.strengthState = strengthState));
    this.store
      .select(selectGameTime)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((gameTime) => this.updateGameTime(gameTime));
    this.store
      .select(selectInterruption_type)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.interruption_type = newValue));
    this.store
      .select(selectGameIncidentReason)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        this.event.gameIncidentReason = newValue;
        this.onGameIncidentChange();
      });
    this.store
      .select(selectGameIncidentSituation)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        this.event.gameIncidentSituation = newValue;
        this.onGameIncidentChange();
      });
    this.store
      .select(selectGameIncidentType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        if (this.event.gameIncidentType !== newValue) {
          this.event.gameIncidentType = newValue;
          this.onGameIncidentTypeChange();
        }
      });
    this.store
      .select(selectOfficialsCallAction)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        this.event.officialsCallAction = newValue;
        this.onGameIncidentChange();
      });
    this.store
      .select(selectOfficialsCallSituation)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        this.event.officialsCallSituation = newValue;
        this.onGameIncidentChange();
      });
    this.store
      .select(selectVideoTag)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.videoTag = newValue));
    this.store
      .select(selectShotOutcome)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(async (newValue) => {
        this.event.shotOutcome = newValue as any;
        await this.handleDraftEvent();
      });
    this.store
      .select(selectNetImpact)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.netImpactX = newValue?.netImpactX));
    this.store
      .select(selectNetImpact)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.netImpactY = newValue?.netImpactY));
    this.store
      .select(selectShotScenario)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.shotScenario = newValue));
    this.store
      .select(selectAttackScenario)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.attackScenario = newValue));
    this.store
      .select(selectShotType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.shotType = newValue));
    this.store
      .select(selectHas_net_traffic)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        if (newValue !== undefined) {
          return (this.event.has_net_traffic = newValue === '1');
        }
      });
    this.store
      .select(selectHas_screen)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        if (newValue !== undefined) {
          return (this.event.has_screen = newValue === '1');
        }
      });
    this.store
      .select(selectTeamFaceOffOutcome)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.teamFaceOffOutcome = newValue));
    this.store
      .select(selectPassType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.pass_type = newValue));
    this.store
      .select(selectPassOutcome)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.pass_outcome = newValue));
    this.store
      .select(selectPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.playerNumber = newValue));
    this.store
      .select(selectPassReceiverPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.pass_receiver = newValue));
    this.store
      .select(selectShotBlockerPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.blocker = newValue));
    this.store
      .select(selectDeflectorPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.deflector = newValue));
    this.store
      .select(selectScreenerPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.screener = newValue));
    this.store
      .select(selectNetTrafficCauserPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.net_traffic_causer = newValue));
    this.store
      .select(selectFaceOffOpponentPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.faceoff_opponent = newValue));
    this.store
      .select(selectFouledPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.fouled_player = newValue));
    this.store
      .select(selectTurnoverOpponentPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.turnover_opponent = newValue));
    this.store
      .select(selectOfficial1Id)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((officialState) => {
        if (!officialState) {
          this.resetOfficials();
          return;
        }
        if (officialState.remove) {
          this.removeOfficialFromEvent(officialState.officialId);
        } else {
          this.addOfficialToEvent(officialState.officialId);
        }
      });
    this.store
      .select(selectOfficial2Id)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((officialState) => {
        if (!officialState) {
          this.resetOfficials();
          return;
        }
        if (officialState.remove) {
          this.removeOfficialFromEvent(officialState.officialId);
        } else {
          this.addOfficialToEvent(officialState.officialId);
        }
      });
    this.store
      .select(selectPenaltyType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(async (newValue) => {
        this.event.penaltyType = newValue;
        if (newValue === 'expiration') {
          await this.handleDraftEvent();
        }
      });
    this.store
      .select(selectPosition)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((position) => {
        if (position) {
          if (position.x && position.y) {
            this.event.xPosition = position.x;
            this.event.yPosition = position.y;
          }
          // when both null set in order to clear
          if (!position.x && !position.y) {
            this.event.xPosition = position.x;
            this.event.yPosition = position.y;
          }
        }
      });
    this.store
      .select(selectPositionPitch)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((positionPitch) => (this.event.positionPitch = positionPitch));
    this.store
      .select(selectPassReceiverPosition)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((position) => {
        if (position) {
          if (position.x && position.y) {
            this.event.receiverXPosition = position.x;
            this.event.receiverYPosition = position.y;
          }
          // when both null set in order to clear
          if (!position.x && !position.y) {
            this.event.receiverXPosition = position.x;
            this.event.receiverYPosition = position.y;
          }
        }
      });
    this.store
      .select(selectFaceoffOutcomePosition)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((position) => {
        if (position) {
          if (position.x && position.y) {
            this.event.faceoffOutcomeXPosition = position.x;
            this.event.faceoffOutcomeYPosition = position.y;
          }
          // when both null set in order to clear
          if (!position.x && !position.y) {
            this.event.faceoffOutcomeXPosition = position.x;
            this.event.faceoffOutcomeYPosition = position.y;
          }
        }
      });
    this.store
      .select(selectShotBlockerPosition)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((position) => {
        if (position) {
          if (position.x && position.y) {
            this.event.blockerXPosition = position.x;
            this.event.blockerYPosition = position.y;
          }
          // when both null set in order to clear
          if (!position.x && !position.y) {
            this.event.blockerXPosition = position.x;
            this.event.blockerYPosition = position.y;
          }
        }
      });
    this.store
      .select(selectHighlightType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.highlightType = newValue));
    this.store
      .select(selectVideoReviewType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.videoReviewType = newValue));
    this.store
      .select(selectHighlightPlayback)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.highlightPlayback = newValue));
    this.store
      .select(selectHighlightRating)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.highlightRating = newValue));
    this.store
      .select(selectPenaltyId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.penaltyId = newValue));
    this.store
      .select(selectPenaltyDuration)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.penaltyDuration = newValue));
    this.store
      .select(selectAssist1PlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.assist1 = newValue));
    this.store
      .select(selectAssist2PlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.event.assist2 = newValue));

    this.broadcast
      .listen()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(async (message) => {
        this.logger.info('broadcast message received', message.data);
        switch (message.data.type) {
          case BroadcastAction.ResetState:
            await this.reset(false);
            break;
          case BroadcastAction.Save:
            const successful = await this.save(false);
            if (successful) {
              await this.reset();
            }
            break;
          default:
            break;
        }
      });
  }

  private startWallClock() {
    if (
      this.game.lagClock &&
      (this.game.status === 'in_collection' ||
        this.game.status === 'in_extended_collection')
    ) {
      interval(1000)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(() => {
          this.gameTimeService.updateElapsedTime(this.event.videoTime);
        });
    }
  }

  private updateVideoTime(newVideoTime: number) {
    this.event.videoTime = newVideoTime;
    this.gameTimeService.updatePeriodAndGameTime(newVideoTime);
    this.puckPossessionStateService.updatePuckPossessionState(newVideoTime);
    this.strengthStateService.updateStrengthState(newVideoTime);
  }

  private updateGameTime(newGameTime: number) {
    this.gameTime = newGameTime;
    this.event.gameTime = newGameTime;
    this.strengthStateService.updateActivePenalties(newGameTime);
    this.shiftsService.updateActiveShifts(newGameTime, this.shifts);
  }

  async saveManually() {
    const successful = await this.save(true);
    if (successful) {
      await this.reset();
    }
  }

  async resetManually() {
    await this.reset();
  }

  private async reset(emitReset: boolean = true) {
    this.resetSharedState(this.event, emitReset);
    await this.resetMode();
  }

  private async resetMode() {
    this.location.replaceState(`/games/${this.game._id}/events/`);
    const gameEventState = await firstValueFrom(
      this.store.select(selectGameEventState)
    );
    const gameState = await firstValueFrom(this.store.select(selectGameState));
    this.event = {
      ...gameEventState,
      gameId: gameState?.gameId,
      teamId:
        gameEventState?.team && gameEventState?.team !== 'neutral'
          ? this.getTeamId(gameEventState?.team)
          : null
    } as unknown as GameEvent;
  }

  private resetSharedState(e: GameEvent, emitReset: boolean = true) {
    const currentPeriod = e.period;
    const currentTeam = e.team;

    this.store.dispatch(resetStateGame({ gameId: this.game._id }));
    this.store.dispatch(
      resetStateGameEvent({ period: currentPeriod, team: currentTeam })
    );

    if (emitReset) {
      this.broadcast.postMessage({ type: BroadcastAction.ResetState });
    }
  }

  async save(ignoreWarnings: boolean): Promise<boolean> {
    if (!this.event || !this.event.eventType) {
      // prevent error message on startup
      return false;
    }

    // A restart picked by hotkey stays in the store when the collector switches
    // to another event type (e.g. r, c, y); keep it off that event.
    if (this.event.eventType !== 'gameRestart') {
      this.leaveOff('gameRestartType');
      this.leaveOff('gameRestartReason');
    }
    if (
      this.event.eventType !== 'penalty' ||
      this.event.penaltyType !== 'start' ||
      !this.event.personalPenalty
    ) {
      this.leaveOff('personalPenalty');
    }
    if (this.event.eventType !== 'turnover') {
      this.leaveOff('turnoverType');
      this.leaveOff('turnoverScenario');
    }
    // Only a position clicked in this edit carries its picture; events without
    // a position carry none.
    if (!this.event.positionPitch || !hasRinkPosition(this.event)) {
      delete this.event.positionPitch;
    }

    if (this.event.eventType === 'shot') {
      if (this.event.has_net_traffic === undefined) {
        this.event.has_net_traffic = false;
      }
      if (this.event.has_screen === undefined) {
        this.event.has_screen = false;
      }
      if (this.event.isAwayTeamEmptyNet === undefined) {
        this.event.isAwayTeamEmptyNet = false;
      }
      if (this.event.isHomeTeamEmptyNet === undefined) {
        this.event.isHomeTeamEmptyNet = false;
      }
    }

    if (this.event.team && this.event.team !== 'neutral') {
      this.event.teamId = this.getTeamId(this.event.team);
    } else {
      this.event.teamId = null;
    }

    if (this.event.playerNumber && this.event.teamId) {
      this.event.playerId = this.getPlayerId(
        this.event.playerNumber,
        this.event.teamId
      );
    } else {
      this.event.playerNumber = null;
      this.event.playerId = null;
    }

    if (this.event.assist1 && this.event.teamId) {
      this.event.assist1Id = this.getPlayerId(
        this.event.assist1,
        this.event.teamId
      );
    } else {
      this.event.assist1 = null;
      this.event.assist1Id = null;
    }

    if (this.event.assist2 && this.event.teamId) {
      this.event.assist2Id = this.getPlayerId(
        this.event.assist2,
        this.event.teamId
      );
    } else {
      this.event.assist2 = null;
      this.event.assist2Id = null;
    }

    if (this.event.blocker && this.event.teamId) {
      this.event.blockerId = this.getPlayerId(
        this.event.blocker,
        this.getOpposingTeamId(this.event.teamId)
      );
    } else {
      this.event.blocker = null;
      this.event.blockerId = null;
    }

    if (this.event.deflector && this.event.teamId) {
      this.event.deflectorId = this.getPlayerId(
        this.event.deflector,
        this.event.teamId
      );
    } else {
      this.event.deflector = null;
      this.event.deflectorId = null;
    }

    if (this.event.screener && this.event.teamId) {
      this.event.screenerId = this.getPlayerId(
        this.event.screener,
        this.event.teamId
      );
    } else {
      this.event.screener = null;
      this.event.screenerId = null;
    }

    if (this.event.net_traffic_causer && this.event.teamId) {
      this.event.net_traffic_causerId = this.getPlayerId(
        this.event.net_traffic_causer,
        this.event.teamId
      );
    } else {
      this.event.net_traffic_causer = null;
      this.event.net_traffic_causerId = null;
    }

    if (this.event.pass_receiver && this.event.teamId) {
      this.event.pass_receiverId = this.getPlayerId(
        this.event.pass_receiver,
        this.event.teamId
      );
    } else {
      this.event.pass_receiver = null;
      this.event.pass_receiverId = null;
    }

    if (this.event.fouled_player && this.event.teamId) {
      this.event.fouled_playerId = this.getPlayerId(
        this.event.fouled_player,
        this.getOpposingTeamId(this.event.teamId)
      );
    } else {
      this.event.fouled_player = null;
      this.event.fouled_playerId = null;
    }

    if (this.event.turnover_opponent && this.event.teamId) {
      this.event.turnover_opponentId = this.getPlayerId(
        this.event.turnover_opponent,
        this.getOpposingTeamId(this.event.teamId)
      );
    } else {
      this.event.turnover_opponent = null;
      this.event.turnover_opponentId = null;
    }

    if (this.event.battle_opponent && this.event.teamId) {
      this.event.battle_opponentId = this.getPlayerId(
        this.event.battle_opponent,
        this.getOpposingTeamId(this.event.teamId)
      );
    } else {
      this.event.battle_opponent = null;
      this.event.battle_opponentId = null;
    }

    if (this.event.takenaway_player && this.event.teamId) {
      this.event.takenaway_playerId = this.getPlayerId(
        this.event.takenaway_player,
        this.getOpposingTeamId(this.event.teamId)
      );
    } else {
      this.event.takenaway_player = null;
      this.event.takenaway_playerId = null;
    }

    if (this.event.eventType === 'interruption') {
      if (this.event.interruption_type === 'period_start') {
        if (this.isRegularPeriod(this.event.period)) {
          this.event.gameTime =
            (parseInt(this.event.period, 10) - 1) *
            this.game.periodLengthInMinutes *
            60;
        }
      }
      if (this.event.interruption_type === 'period_end') {
        if (this.isRegularPeriod(this.event.period)) {
          this.event.gameTime =
            parseInt(this.event.period, 10) *
            this.game.periodLengthInMinutes *
            60;
        }
      }
    }

    const isAllowedDraftEvent = this.eventService.isAllowedDraftEvent(
      this.game,
      this.event.eventType
    );
    const errorMessage = this.eventValidationService.validate(
      this.event,
      this.game
    );
    if (errorMessage) {
      if (this.event._id || !isAllowedDraftEvent) {
        this.broadcast.postMessage({
          type: BroadcastAction.SaveFailed,
          error: errorMessage
        });
        this.alertService.showError('Event is not valid: ' + errorMessage);
        return false;
      } else {
        this.logger.info('Event is draft and invalid: ' + errorMessage);
      }
    }

    let hasBeenDraft = false;
    if (this.event._id && this.event.draft && !errorMessage) {
      hasBeenDraft = true;
      this.event.draft = false;
    } else {
      if (errorMessage && isAllowedDraftEvent) {
        this.event.draft = true;
      }
    }

    this.event.gameId = this.game._id;
    if (
      (!this.event._id &&
        this.event.eventType === 'face_off' &&
        !this.event.draft) ||
      (this.event._id && hasBeenDraft && this.event.eventType === 'face_off')
    ) {
      const otherEvent = this.createCorrespondingEventForOtherTeam();
      try {
        const e = await this.saveSingleEvent(otherEvent, ignoreWarnings);
        this.eventsComponent.addEvent(e);
      } catch (error) {
        this.broadcast.postMessage({
          type: BroadcastAction.SaveFailed,
          error: errorMessage
        });
        this.alertService.showError(
          'Saving opponent event failed: ' + error.message
        );
        return false;
      }
    }
    const eventDeepCopy = JSON.parse(JSON.stringify(this.event));
    try {
      const e = await this.saveSingleEvent(eventDeepCopy, ignoreWarnings);
      this.eventsComponent.addEvent(e);
    } catch (error) {
      this.broadcast.postMessage({
        type: BroadcastAction.SaveFailed,
        error: error.message
      });
      this.alertService.showError('Saving event failed: ' + error.message);
      return false;
    }

    return true;
  }

  private createCorrespondingEventForOtherTeam() {
    this.event.faceoffId = ObjectId();
    const otherEvent = JSON.parse(JSON.stringify(this.event));
    delete otherEvent._id; // draft event contains _id
    if (this.event.faceoff_opponent != null) {
      this.updateFaceOffDetails(otherEvent);
    }
    if (this.game != null) {
      otherEvent.team =
        this.event.team === this.game.homeTeam
          ? this.game.awayTeam
          : this.game.homeTeam;
      otherEvent.teamId = this.getTeamId(otherEvent.team);
      otherEvent.playerId = this.getPlayerId(
        otherEvent.playerNumber,
        otherEvent.teamId
      );
    }
    this.logger.info('other event: ' + otherEvent.toString());
    return otherEvent;
  }

  private updateFaceOffDetails(otherEvent: GameEvent) {
    otherEvent.playerNumber = this.event.faceoff_opponent;
    otherEvent.faceoff_opponent = this.event.playerNumber;
    if (this.event.teamFaceOffOutcome === 'win') {
      otherEvent.teamFaceOffOutcome = 'lost';
    } else if (this.event.teamFaceOffOutcome === 'lost') {
      otherEvent.teamFaceOffOutcome = 'win';
    }
  }

  private async saveSingleEvent(
    event: GameEvent,
    ignoreWarnings: boolean
  ): Promise<GameEvent> {
    const warnings = this.eventValidationService.getWarnings(event, this.game);
    if (warnings && !ignoreWarnings) {
      this.alertService.showWarning(warnings);
    }

    // purge attributes that should not be saved or exported
    delete event['penalties'];
    delete event['videoStatus'];
    delete event['isInterrupted'];

    return this.eventService
      .save(event)
      .pipe(
        tap((e) => {
          this.broadcast.postMessage({ type: BroadcastAction.SaveComplete });
          this.alertService.showInfo(
            `Event saved${this.getMessageForPenaltyPairedGameIncident()}`
          );
          if (event.eventType === 'penalty') {
            this.strengthStateService.addAndDispatchActivePenalty(
              e,
              e.gameTime
            );
          } else if (isClockEvent(event)) {
            this.gameTimeService.addAndDispatchTimeEvent(e, e.videoTime);
          } else if (event.eventType === 'puckPossession') {
            this.puckPossessionStateService.addAndDispatchPuckPossessionEvent(
              e,
              e.videoTime
            );
          }
        })
      )
      .toPromise();
  }

  private getMessageForPenaltyPairedGameIncident() {
    if (
      this.event.eventType === 'penalty' &&
      this.event.penaltyType === 'start' &&
      this.game.officiatingDataSet
    ) {
      return this.isEditMode
        ? '. Paired game incident updated.'
        : '. Paired game incident created.';
    }
    return '';
  }

  private getTeamId(teamName): string {
    if (this.game.homeTeam === teamName) {
      return this.game.homeTeamId;
    } else if (this.game.awayTeam === teamName) {
      return this.game.awayTeamId;
    } else {
      throw new Error('Could not find team for name: ' + teamName);
    }
  }

  private getOpposingTeamId(teamId): string {
    return teamId === this.game.homeTeamId
      ? this.game.awayTeamId
      : this.game.homeTeamId;
  }

  private getPlayerId(playerNumber, teamId): string {
    const homeTeamOrAwayTeam = teamId === this.game.homeTeamId;
    return this.game.getPlayerIdByJerseyNumber(
      homeTeamOrAwayTeam,
      playerNumber
    );
  }

  editEvent(event) {
    this.eventService
      .getEvent(this.game._id, event._id)
      .subscribe((pbpEvent) => this.editSavedEvent(pbpEvent));
  }

  seekEvent(event: GameEvent) {
    this.event.videoTime = event.videoTime;
    this.store.dispatch(
      videoTimeExternalChange({
        videoTimeExternal: event.videoTime,
        random: self.crypto.randomUUID()
      })
    );
  }

  editSavedEvent(event: GameEvent) {
    this.event = event;
    this.savedEvent = { ...event };
    this.location.replaceState(
      `/games/${this.game._id}/events/${this.event._id}`
    );

    this.store.dispatch(eventIdChange({ _id: event._id }));
    this.store.dispatch(gameIdChange({ gameId: event.gameId }));
    this.store.dispatch(eventTypeChange({ eventType: event.eventType }));
    this.store.dispatch(
      oddMenRushDetailChange({ oddMenRushDetail: event.oddMenRushDetail })
    );
    this.store.dispatch(
      defensiveReactionChange({ defensiveReaction: event.defensiveReaction })
    );
    this.store.dispatch(
      gameRestartTypeChange({ gameRestartType: event.gameRestartType })
    );
    this.store.dispatch(
      gameRestartReasonChange({ gameRestartReason: event.gameRestartReason })
    );
    this.store.dispatch(
      turnoverTypeChange({ turnoverType: event.turnoverType })
    );
    this.store.dispatch(
      turnoverScenarioChange({ turnoverScenario: event.turnoverScenario })
    );
    this.store.dispatch(
      turnoverOpponentPlayerNumberChange({
        turnoverOpponentPlayerNumber: event.turnover_opponent
      })
    );
    this.store.dispatch(
      videoTimeExternalChange({
        videoTimeExternal: event.videoTime,
        random: self.crypto.randomUUID()
      })
    );
    this.store.dispatch(teamChange({ team: event.team }));
    this.store.dispatch(
      playerNumberChange({ playerNumber: event.playerNumber })
    );
    this.store.dispatch(
      teamFaceoffOutcomeChange({ teamFaceOffOutcome: event.teamFaceOffOutcome })
    );

    this.store.dispatch(
      faceoffOpponentPlayerNumberChange({
        faceOffOpponentPlayerNumber: event.faceoff_opponent
      })
    );

    this.store.dispatch(shotOutcomeChange({ shotOutcome: event.shotOutcome }));
    this.store.dispatch(
      shotScenarioChange({ shotScenario: event.shotScenario })
    );
    this.store.dispatch(shotTypeChange({ shotType: event.shotType }));

    this.store.dispatch(
      deflectorPlayerNumberChange({ deflectorPlayerNumber: event.deflector })
    );

    this.store.dispatch(
      assist1PlayerNumberChange({
        assist1PlayerNumber: event.assist1
      })
    );

    this.store.dispatch(
      assist2PlayerNumberChange({
        assist2PlayerNumber: event.assist2
      })
    );

    // this.store.dispatch(isBlockedChange({ is_blocked: event.is_blocked}));  // TODO remove unused field!
    this.store.dispatch(
      shotBlockerPlayerNumberChange({ shotBlockerPlayerNumber: event.blocker })
    );

    this.store.dispatch(
      hasNetTrafficChange({
        has_net_traffic: event.has_net_traffic ? '1' : '0'
      })
    );

    this.store.dispatch(
      netTrafficCauserPlayerNumberChange({
        netTrafficCauserPlayerNumber: event.net_traffic_causer
      })
    );
    this.store.dispatch(
      hasScreenChange({ has_screen: event.has_screen ? '1' : '0' })
    );

    this.store.dispatch(
      screenerPlayerNumberChange({ screenerPlayerNumber: event.screener })
    );

    this.store.dispatch(
      fouledPlayerNumberChange({ fouledPlayerNumber: event.fouled_player })
    );

    this.store.dispatch(passTypeChange({ passType: event.pass_type }));

    this.store.dispatch(
      passReceiverPlayerNumberChange({
        passReceiverPlayerNumber: event.pass_receiver
      })
    );

    this.store.dispatch(passOutcomeChange({ passOutcome: event.pass_outcome }));

    this.store.dispatch(
      interruptionTypeChange({ interruption_type: event.interruption_type })
    );

    this.store.dispatch(videoTagChange({ videoTag: event.videoTag }));

    this.store.dispatch(periodChange({ period: event.period }));
    this.store.dispatch(
      highlightTypeChange({ highlightType: event.highlightType })
    );
    this.store.dispatch(
      highlightPlaybackChange({ highlightPlayback: event.highlightPlayback })
    );
    this.store.dispatch(
      highlightRatingChange({ highlightRating: event.highlightRating })
    );
    // must come before other game incident actions (as it resets current state completely)
    this.store.dispatch(
      gameIncidentTypeChange({ gameIncidentType: event.gameIncidentType })
    );
    this.store.dispatch(
      gameIncidentReasonChange({ gameIncidentReason: event.gameIncidentReason })
    );
    this.store.dispatch(
      gameIncidentSituationChange({
        gameIncidentSituation: event.gameIncidentSituation
      })
    );
    this.store.dispatch(
      officialsCallActionChange({
        officialsCallAction: event.officialsCallAction
      })
    );
    this.store.dispatch(
      officialsCallSituationChange({
        officialsCallSituation: event.officialsCallSituation
      })
    );
    const assignedOfficialId1 = (event.assignedOfficialIds ?? [])[0];
    const assignedOfficialId2 = (event.assignedOfficialIds ?? [])[1];
    if (Number.isInteger(assignedOfficialId1)) {
      this.store.dispatch(
        official1NumberChange({
          officialId: assignedOfficialId1
        })
      );
    }
    if (Number.isInteger(assignedOfficialId2)) {
      this.store.dispatch(
        official2NumberChange({
          officialId: assignedOfficialId2
        })
      );
    }
    if (event.xPosition !== null && event.yPosition !== null) {
      this.store.dispatch(
        positionChange({ position: { x: event.xPosition, y: event.yPosition } })
      );
    }
    if (event.receiverXPosition !== null && event.receiverYPosition !== null) {
      this.store.dispatch(
        passReceiverPositionChange({
          passReceiverPosition: {
            x: event.receiverXPosition,
            y: event.receiverYPosition
          }
        })
      );
    }
    if (event.blockerXPosition !== null && event.blockerYPosition !== null) {
      this.store.dispatch(
        shotBlockerPositionChange({
          shotBlockerPosition: {
            x: event.blockerXPosition,
            y: event.blockerYPosition
          }
        })
      );
    }
    if (
      event.faceoffOutcomeXPosition !== null &&
      event.faceoffOutcomeYPosition !== null
    ) {
      this.store.dispatch(
        faceoffOutcomePositionChange({
          faceoffOutcomePosition: {
            x: event.faceoffOutcomeXPosition,
            y: event.faceoffOutcomeYPosition
          }
        })
      );
    }
    this.store.dispatch(
      positionPitchChange({ positionPitch: event.positionPitch })
    );
  }

  onTeamChange(team: string): void {
    if (!team) {
      return;
    }

    if (this.game.homeTeam === team) {
      this.players = Players.orderPlayerStringByJerseyNumber(
        this.homeTeamPlayers
      );
      this.opponentPlayers = Players.orderPlayerStringByJerseyNumber(
        this.awayTeamPlayers
      );
    } else if (this.game.awayTeam === team) {
      this.players = Players.orderPlayerStringByJerseyNumber(
        this.awayTeamPlayers
      );
      this.opponentPlayers = Players.orderPlayerStringByJerseyNumber(
        this.homeTeamPlayers
      );
    }

    // switch player to same jersey number of selected team
    if (this.event.playerNumber) {
      if (!this.players.includes(this.event.playerNumber)) {
        const [jerseyNumber, _] = this.event.playerNumber.split(' - ');
        this.event.playerNumber = this.gameService.findPlayerByJerseyNumber(
          this.game,
          team,
          jerseyNumber
        );
        this.store.dispatch(
          playerNumberChange({ playerNumber: this.event.playerNumber })
        );
      }
    }
  }

  onPeriodChange(period: GamePeriod) {
    this.event.period = period;
    this.store.dispatch(periodChange({ period }));
  }

  private handleTeamMessage(newValue) {
    if (newValue == null) {
      return;
    }
    this.event.team = newValue;
    this.onTeamChange(this.event.team);
  }

  private initForGame(game: Game) {
    if (game && game._id) {
      this.game = game;
      this.event.gameId = game._id;
      this.players = Players.orderPlayerStringByJerseyNumber(
        this.gameService.getAllPlayers(game)
      );
      this.homeTeamPlayers = Players.orderPlayerStringByJerseyNumber(
        this.gameService.getHomeTeamPlayers(game)
      );
      this.awayTeamPlayers = Players.orderPlayerStringByJerseyNumber(
        this.gameService.getAwayTeamPlayers(game)
      );
      this.filterablePlayers = game.getAllPlayersObj();
      this.teams = this.gameService.getTeamNames(game);
      this.teams.push(Team.NEUTRAL_TEAM);
      this.title.setTitle(
        this.game.homeTeam + '-' + this.game.awayTeam + ' > Game Actions'
      );

      this.shiftsService
        .getShifts(game)
        .subscribe((shifts) => (this.shifts = shifts));
      this.videoService.prepareMediaData(this.game.videos ?? []);
      this.allOfficials = game.officials;
    } else {
      this.players = [];
      this.homeTeamPlayers = [];
      this.awayTeamPlayers = [];
      this.teams = [];
    }
  }

  private fetchInfractionTypes() {
    const isSihfGame = !!this.game.gameId;
    this.domainService.fetchInfractionTypes().subscribe((response) => {
      this.infractionTypes = response.map((t) => {
        const sihfCodeOrEmpty =
          isSihfGame && t.sihfFoulCode !== t.iihfRuleNo
            ? `(${t.sihfFoulCode})`
            : '';
        const type = {
          key: t.key,
          value: `${t.iihfRuleNo ? t.iihfRuleNo + ' - ' : ''}${
            t.key
          } ${sihfCodeOrEmpty}`.trim()
        };
        return type;
      });
    });
  }

  isGameSet() {
    return this.game != null;
  }

  prepareVideoPlayerTrimDialog(
    videoClip: VideoClip,
    videoClipType: VideoClipType
  ) {
    this.videoClipType = videoClipType;
    this.videoTrim = this.findVideoForTrimming(videoClip);
    if (!this.videoTrim) {
      this.alertService.showError(
        `Couldn't find a video to trim. Requires a video with camera angle "tvfeed" or "main".`
      );
    }
    this.cameraAngle = this.videoTrim?.cameraAngle;
    this.format = this.videoTrim?.format;
    this.variant = this.videoTrim?.variant;
  }

  openVideoPlayerTrimDialog(duration: number) {
    const cameraIndex = this.findVideoCameraIndex();
    const data = this.prepareDataForTrim(duration);
    this.videoTrim = null;
    const dialogRef = this.dialog.open(VideoPlayerTrimDialogComponent, {
      width: '1200px',
      height: '850px',
      maxWidth: '1200px',
      maxHeight: '850px',
      data: {
        game: this.game,
        cameraIndex,
        src: data.videoUrl,
        startOfVideoClip: data.startOfExtendedVideoClip,
        durationVideoClip: data.durationOfExtendedVideoClip,
        startOfOriginalVideoClip: data.startOfOriginalVideoClip,
        durationOfOriginalVideoClip: data.durationOfOriginalVideoClip,
        durationOfFullVideo: duration
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.startVideoTime && result.endVideoTime) {
          if (this.videoClipType === VideoClipType.GOAL_SLOW_CLIP) {
            if (!this.event.goalClip) {
              this.event.goalClip = {};
            }
            this.setVideoClipProperties(this.event.goalClip, result);
          } else if (this.videoClipType === VideoClipType.VIDEO_REVIEW_CLIP) {
            if (!this.event.videoReviewClip) {
              this.event.videoReviewClip = {};
            }
            this.setVideoClipProperties(this.event.videoReviewClip, result);
          }
        } else if (result.cameraAngle) {
          // switch camera
          this.cameraAngle = result.cameraAngle;
          let videoClip;
          if (this.videoClipType === VideoClipType.GOAL_SLOW_CLIP) {
            videoClip = this.event.goalClip;
          } else if (this.videoClipType === VideoClipType.VIDEO_REVIEW_CLIP) {
            videoClip = this.event.videoReviewClip;
          }
          this.prepareVideoPlayerTrimDialog(videoClip, this.videoClipType);
        }
      }
    });
  }

  setVideoClipProperties(videoClip: VideoClip, result: any): void {
    videoClip.startVideoTime = result.startVideoTime;
    videoClip.endVideoTime = result.endVideoTime;
    videoClip.cameraAngle = this.cameraAngle;
    videoClip.format = this.format;
    videoClip.variant = this.variant;
    if (!videoClip.exportStatus) {
      videoClip.exportStatus = { status: 'not_started' };
    }
  }

  errorVideoPlayerTrimDialog(error: string) {
    this.videoTrim = null;
    const videos = this.game?.videos;
    const dialogRef = this.dialog.open(CameraAnglesSelectDialogComponent, {
      width: '550px',
      data: {
        message: `Video issue with ${this.cameraAngle}, try other camera angle`,
        videos
      }
    });
    this.logger.error('Could not trim video', error);
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.cameraAngle) {
        this.cameraAngle = result.cameraAngle;
        let videoClip;
        if (this.videoClipType === VideoClipType.GOAL_SLOW_CLIP) {
          videoClip = this.event.goalClip;
        }
        this.prepareVideoPlayerTrimDialog(videoClip, this.videoClipType);
      }
    });
  }

  prepareDataForTrim(duration: number) {
    const videoClipData = this.videoService.getVideoClipData(
      this.event,
      this.videoClipType
    );
    if (!videoClipData) {
      this.alertService.showError(`Couldn't find a video type to trim.`);
    }
    const { startVideoClipTimeSequence, durationVideoClipSequence } =
      this.videoService.getVideoClipSequence(
        videoClipData.startVideoClipTime,
        videoClipData.endVideoClipTime,
        this.event.videoTime,
        this.videoTrim.offset,
        this.videoClipType
      );
    // Duration of whole video is important for achieving extended time before and after sequence
    const durationOfFullVideo = duration;

    return {
      startOfOriginalVideoClip: startVideoClipTimeSequence,
      durationOfOriginalVideoClip: durationVideoClipSequence,
      ...this.videoService.prepareVideoDataForTrimming(
        this.videoTrim.urlSigned,
        startVideoClipTimeSequence,
        durationVideoClipSequence,
        durationOfFullVideo
      )
    };
  }

  showSummary(): void {
    this.dialog.open(PlayerSummaryComponent, {
      width: '640px',
      data: {
        game: this.game
      }
    });
  }

  updateEvent(event: GameEvent) {
    if (event.eventType === 'penalty') {
      this.strengthStateService.addAndDispatchActivePenalty(
        event,
        this.gameTime
      );
      this.strengthStateService.updateStrengthState(this.event.videoTime);
    }

    if (isClockEvent(event)) {
      this.gameTimeService.addAndDispatchTimeEvent(event, this.event.videoTime);
    } else if (event.eventType === 'puckPossession') {
      this.puckPossessionStateService.addAndDispatchPuckPossessionEvent(
        event,
        this.event.videoTime
      );
    }

    if (event.eventType === 'time_on_ice') {
      const shiftOn = this.shifts.find((s) => s.onEvent._id === event._id);
      const shiftOff = this.shifts.find(
        (s) => s.offEvent && s.offEvent._id === event._id
      );

      if (shiftOn || shiftOff) {
        // existing shift - TOI event has been updated
        this.shiftsService.updateActiveShifts(this.gameTime, this.shifts);
        return;
      }

      if (event.timeOnIceType === 'on') {
        const shift = {
          player: this.game.getPlayerObj(event.playerNumber, event.team),
          onEvent: event,
          offEvent: null,
          duration: null
        } as Shift;
        this.shifts.push(shift);
      } else if (event.timeOnIceType === 'off') {
        const shift = this.shifts.find(
          (s) =>
            s.onEvent.playerId === event.playerId &&
            !s.offEvent &&
            s.onEvent.period === event.period &&
            s.onEvent.gameTime <= event.gameTime
        );
        if (!shift) {
          this.logger.warn(
            'Could not find existing shift for TOI off event',
            event
          );
          return;
        }
        shift.offEvent = event;
        shift.duration = event.gameTime - shift.onEvent.gameTime;
      }

      this.shiftsService.updateActiveShifts(this.gameTime, this.shifts);
    }
  }

  onEventTypeChange(eventType: EventType) {
    this.event.eventType = eventType;
    this.resetSharedState(this.event);

    if (this.event.eventType === 'penalty' && !this.event.penaltyType) {
      this.event.penaltyType = 'start';
    }
    this.event._id = null;
  }

  get isEditMode() {
    return !!this.event._id;
  }

  async savingDraftEvent(): Promise<void> {
    this.logger.info(`Create live draft event ${this.event.eventType}`);
    const successful = await this.save(false);
    if (successful) {
      await this.reset();
    }
  }

  async handleDraftEvent(): Promise<void> {
    if (
      this.eventService.shouldSaveDraftEvent(
        this.isEditMode,
        this.game,
        this.event
      )
    ) {
      await this.savingDraftEvent();
    }
  }

  exportGoalClip(event: GameEvent) {
    event.goalClip.exportStatus = {
      status: 'pending',
      timeoutAt: new Date(Date.now() + 10 * 60 * 1000)
    };
    this.eventService.exportGoalClips(event.gameId, [event._id]).subscribe({
      next: ({ message }) => {
        this.alertService.showInfo(message);
      },
      error: (httpError) => {
        this.alertService.showError(
          'Export goal clip failed: ' + httpError.error.message
        );
        event.goalClip.exportStatus = undefined;
      }
    });
  }

  renotifyNLGoalClip(event: GameEvent) {
    this.eventService.renotifyNLGoalClip(event._id).subscribe({
      next: ({ message }) => {
        this.alertService.showInfo(message || 'NL notification sent');
      },
      error: (httpError) => {
        this.alertService.showError(
          'Re-notify NL failed: ' + httpError.error?.message
        );
      }
    });
  }

  exportViewReviewClip(event: GameEvent) {
    event.videoReviewClip.exportStatus = {
      status: 'pending',
      timeoutAt: new Date(Date.now() + 10 * 60 * 1000)
    };
    this.eventService
      .exportVideoReviewClips(event.gameId, [event._id])
      .subscribe({
        next: ({ message }) => {
          this.alertService.showInfo(message);
        },
        error: (httpError) => {
          this.alertService.showError(
            'Export highlight video review failed: ' + httpError.error.message
          );
          event.videoReviewClip.exportStatus = undefined;
        }
      });
  }

  findVideoForTrimming(videoClip: VideoClip) {
    const format = this.format ?? videoClip?.format;
    const variant = this.variant ?? videoClip?.variant;
    const cameraAngle = this.cameraAngle ?? videoClip?.cameraAngle;

    if (cameraAngle) {
      // already saved goal - find video
      if (format && variant) {
        return this.game.videos.find(
          (v) =>
            v.cameraAngle === cameraAngle &&
            v.format === format &&
            v.variant === variant
        );
      }
      // switching between cameras for unsaved goal
      return this.game.videos.find((v) => v.cameraAngle === cameraAngle);
    }

    // default behavior
    const tvFeed = this.game.videos.find(
      (v) => v.cameraAngle === CameraAngle.TV_FEED
    );
    if (tvFeed) {
      return tvFeed;
    }

    const main = this.game.videos.find(
      (v) => v.cameraAngle === CameraAngle.MAIN
    );
    if (main) {
      return main;
    }

    return;
  }

  findVideoCameraIndex() {
    if (this.format && this.variant) {
      return this.game.videos.findIndex(
        (v) =>
          v.cameraAngle === this.cameraAngle &&
          v.format === this.format &&
          v.variant === this.variant
      );
    } else {
      return this.game.videos.findIndex(
        (v) => v.cameraAngle === this.cameraAngle
      );
    }
  }

  private async resetHard() {
    this.resetHardSharedState();
    await this.resetMode();
  }

  private resetHardSharedState() {
    this.store.dispatch(resetHardStateGame({ gameId: this.game._id }));
    this.store.dispatch(resetHardStateGameEvent());
    this.broadcast.postMessage({ type: BroadcastAction.ResetState });
  }

  private isRegularPeriod(period: GamePeriod): boolean {
    return parseInt(period, 10) <= regularPeriodCount(this.game?.sport);
  }

  get interruptionReasons(): InterruptionType[] {
    return this.gameEventInterruptionTypeService.collectedInterruptionTypes(
      this.game?.sport
    );
  }

  getInterruptionReasonLabel(interruptionType: InterruptionType) {
    return this.gameEventInterruptionTypeService.getInterruptionTypeLabel(
      interruptionType,
      this.game?.sport
    );
  }

  hasOfficiatingCollection() {
    return this.game.hasOfficiatingCollection();
  }

  hasCallableGameIncidentType() {
    return this.gameIncidentService.isCallableGameIncidentType(
      this.event.gameIncidentType
    );
  }

  isShowGameIncidentUncalledPenaltySeverity() {
    return (
      this.hasOfficiatingCollection() &&
      this.gameIncidentService.isWrongUncalledPenaltyEvent(this.event)
    );
  }

  isShowGameIncidentPenalty() {
    return (
      this.hasOfficiatingCollection() &&
      this.event.gameIncidentType === 'penalty'
    );
  }

  isShowGameIncidentSituation() {
    return (
      this.hasOfficiatingCollection() &&
      this.event.gameIncidentType === 'situation'
    );
  }

  isShowGameIncidentPlaceholder() {
    return (
      !this.isShowGameIncidentPenalty() && !this.isShowGameIncidentSituation()
    );
  }

  isShowGameIncidentPenaltyAttributes() {
    return (
      this.hasOfficiatingCollection() &&
      this.gameIncidentService.isPenaltyEvent(this.event)
    );
  }

  isShowGameIncidentCalledPenaltySeverity() {
    return (
      this.hasOfficiatingCollection() &&
      this.gameIncidentService.isCalledPenaltyEvent(this.event)
    );
  }

  isShowGameIncidentIcingAttributes() {
    return (
      this.hasOfficiatingCollection() &&
      this.gameIncidentService.isIcingEvent(this.event)
    );
  }

  onGameIncidentTypeChange() {
    this.gameIncidentService.resetValues(this.event);
  }

  onGameIncidentChange() {
    this.gameIncidentService.applyDefaultValues(this.event);
    this.populateFilteredOfficials();
  }

  populateFilteredOfficials() {
    if (this.event.gameIncidentType === 'penalty') {
      this.filteredOfficials = this.allOfficials.filter(
        (o) => o.officialRoleInGame === 'head'
      );
    } else if (
      this.event.gameIncidentType === 'offside' ||
      this.event.gameIncidentType === 'icing'
    ) {
      this.filteredOfficials = this.allOfficials.filter(
        (o) => o.officialRoleInGame === 'linesperson'
      );
    } else {
      this.filteredOfficials = [];
    }
  }

  isTeamDisabled() {
    const teamDisabledEventTypes: EventType[] = ['game_incident'];
    return teamDisabledEventTypes.includes(this.event?.eventType);
  }

  isPlayerDisabled() {
    const playerDisabledEventTypes: EventType[] = ['game_incident'];
    return playerDisabledEventTypes.includes(this.event?.eventType);
  }

  isIceRinkVisible() {
    const iceRinkNotVisibleEventTypes: EventType[] = ['game_incident'];
    return !iceRinkNotVisibleEventTypes.includes(this.event?.eventType);
  }

  addOfficialToEvent(officialId: number) {
    if (!this.event?.assignedOfficialIds) {
      this.event.assignedOfficialIds = [officialId];
    } else if (!this.event.assignedOfficialIds.includes(officialId)) {
      this.event.assignedOfficialIds.push(officialId);
    }
  }

  removeOfficialFromEvent(officialId: number) {
    this.event.assignedOfficialIds = this.event.assignedOfficialIds.filter(
      (o) => o !== officialId
    );
  }

  resetOfficials() {
    this.event.assignedOfficialIds = [];
  }

  populateDefaultOfficials() {
    this.populateFilteredOfficials();
    this.resetOfficials();
    const officialIds = this.filteredOfficials.map((o) => o.officialId);
    if (this.event.gameIncidentType === 'icing') {
      this.event.assignedOfficialIds = officialIds;
    }
    if (this.event.gameIncidentType === 'offside' && officialIds.length === 1) {
      this.event.assignedOfficialIds = [officialIds[0]];
    }
    if (this.event.gameIncidentType === 'penalty' && officialIds.length === 1) {
      this.event.assignedOfficialIds = [officialIds[0]];
    }
  }

  isCallableGameIncidentEvent() {
    return this.gameIncidentService.isCallableGameIncidentType(
      this.event.gameIncidentType
    );
  }

  onOfficialActionChanged() {
    if (!this.event.officialsCallAction) {
      this.resetOfficials();
    } else if (this.event.assignedOfficialIds?.length === 0) {
      this.populateDefaultOfficials();
    }
  }
}
